import { AlertTriangle, FileSpreadsheet, Upload } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  BaselineImportError,
  type PreviewResult,
  useCommitBaseline,
  usePreviewBaseline,
  useReplaceBaseline,
} from "@/hooks/use-baseline-import";

interface BaselineUploadModalProps {
  solutionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "import" creates a baseline from scratch (POST /commit). "replace" wipes the existing
   *  unconfirmed baseline (POST /replace). */
  mode: "import" | "replace";
  existingCounts?: { deliverables: number; epics: number; stories: number } | null;
}

/**
 * Outer shell delegates to an inner component that only mounts while `open`.
 * That gives us free state reset on every open without needing useEffect —
 * the inner state lifetime is exactly one open/close cycle.
 */
export function BaselineUploadModal(props: BaselineUploadModalProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-2xl">{props.open && <ModalBody {...props} />}</DialogContent>
    </Dialog>
  );
}

function ModalBody({ solutionId, onOpenChange, mode, existingCounts }: BaselineUploadModalProps) {
  const preview = usePreviewBaseline(solutionId);
  const commit = useCommitBaseline(solutionId);
  const replace = useReplaceBaseline(solutionId);
  const writer = mode === "replace" ? replace : commit;

  const [file, setFile] = useState<File | null>(null);
  const [csvText, setCsvText] = useState<string>("");
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);

  async function onFileChosen(f: File | null) {
    setFile(f);
    setPreviewResult(null);
    if (!f) {
      setCsvText("");
      return;
    }
    try {
      const text = await f.text();
      setCsvText(text);
      const result = await preview.mutateAsync(text);
      setPreviewResult(result);
    } catch (err) {
      if (err instanceof BaselineImportError) {
        toast.error(err.message);
      } else {
        toast.error("Failed to read file");
      }
    }
  }

  async function onImport() {
    if (!csvText || !previewResult || previewResult.errors.length > 0) return;
    try {
      await writer.mutateAsync(csvText);
      onOpenChange(false);
    } catch {
      // Toasted by the mutation's onError unless validation; in that case
      // the modal stays open so the user sees what went wrong.
    }
  }

  const hasErrors = (previewResult?.errors.length ?? 0) > 0;
  const canImport = previewResult !== null && !hasErrors && !preview.isPending && !writer.isPending;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display">
          {mode === "replace" ? "Replace baseline" : "Upload baseline CSV"}
        </DialogTitle>
        <DialogDescription>
          {mode === "replace"
            ? "Drop in a corrected CSV. The current unconfirmed baseline will be deleted and re-inserted in one transaction."
            : "Upload a 17-column baseline CSV (US # · Story · User Group · I Want · So That · Acceptance Criteria · Priority · Size · Status · Sprint · Target Date · Owner · L1 – ID · L1 – Deliverable · L2 – ID · L2 – Epic · L1 – Group). Codes use the PREFIX-NNN form (e.g. D-001, US-81)."}
        </DialogDescription>
      </DialogHeader>

      {mode === "replace" && existingCounts && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/50 px-3 py-2 text-xs text-amber-900 dark:text-amber-100 flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            This will delete the existing baseline ({existingCounts.deliverables} deliverable
            {existingCounts.deliverables === 1 ? "" : "s"}, {existingCounts.epics} epic
            {existingCounts.epics === 1 ? "" : "s"}, {existingCounts.stories} stor
            {existingCounts.stories === 1 ? "y" : "ies"}) and import the new CSV in one step. Only
            allowed before the baseline is confirmed.
          </span>
        </div>
      )}

      {/* File picker */}
      <div className="rounded-lg border border-dashed border-[var(--color-border)] p-4 space-y-2">
        <label
          htmlFor="baseline-csv-input"
          className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold block"
        >
          CSV file
        </label>
        <input
          id="baseline-csv-input"
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => onFileChosen(e.target.files?.[0] ?? null)}
          className="text-sm w-full"
        />
        {file ? (
          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
            <FileSpreadsheet className="h-3.5 w-3.5" />
            {file.name} — {(file.size / 1024).toFixed(1)} KB
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            Choose a baseline CSV with 17 columns (US #, Story, …, L1 – Group).
          </div>
        )}
      </div>

      {/* Preview */}
      {preview.isPending && <div className="text-xs text-muted-foreground">Validating CSV…</div>}

      {previewResult && !hasErrors && (
        <div className="space-y-3">
          <h4 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
            Preview
          </h4>
          <div className="grid grid-cols-4 gap-2">
            <CountTile label="Deliverables" count={previewResult.counts.deliverables} />
            <CountTile label="Epics" count={previewResult.counts.epics} />
            <CountTile label="Stories" count={previewResult.counts.stories} />
            <CountTile label="ACs" count={previewResult.counts.acceptanceCriteria} />
          </div>
          <p className="text-xs text-muted-foreground">
            No errors found. Click <span className="font-semibold">Import</span> to load this
            baseline into the solution.
          </p>
        </div>
      )}

      {previewResult && hasErrors && (
        <div className="space-y-2">
          <h4 className="text-[11px] uppercase tracking-[0.14em] text-destructive font-semibold flex items-center gap-1.5">
            <AlertTriangle className="h-3 w-3" />
            {previewResult.errors.length} error{previewResult.errors.length === 1 ? "" : "s"} — fix
            the CSV and re-upload
          </h4>
          <div className="border border-[var(--color-border)] rounded-md overflow-hidden max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5 font-medium w-12">Row</th>
                  <th className="text-left px-2 py-1.5 font-medium w-40">Column</th>
                  <th className="text-left px-2 py-1.5 font-medium">Message</th>
                </tr>
              </thead>
              <tbody>
                {previewResult.errors.map((err) => (
                  <tr
                    key={`${err.row}-${err.column ?? ""}-${err.message}`}
                    className="border-t border-[var(--color-border)]"
                  >
                    <td className="px-2 py-1.5 font-mono tabular-nums">
                      {err.row === 0 ? "header" : err.row}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-muted-foreground">
                      {err.column ?? "—"}
                    </td>
                    <td className="px-2 py-1.5">{err.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={writer.isPending}>
          Cancel
        </Button>
        <Button onClick={onImport} disabled={!canImport}>
          <Upload className="h-3.5 w-3.5 mr-1.5" />
          {writer.isPending
            ? mode === "replace"
              ? "Replacing…"
              : "Importing…"
            : mode === "replace"
              ? "Replace baseline"
              : "Import"}
        </Button>
      </DialogFooter>
    </>
  );
}

function CountTile({ label, count }: { label: string; count: number }) {
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-card p-2.5 text-center">
      <div className="text-lg font-display font-semibold tabular-nums">{count}</div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mt-0.5">
        {label}
      </div>
    </div>
  );
}
